import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker, Input, Table, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { F911 } from '../../../services/pw9220Api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface PunchLogTabProps {
  punchLogs: F911[];
  punchLogTotal: number;
  loading: boolean;
  onLoad: (params: {
    employee_code?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    size?: number;
  }) => void;
}

const LOG_CODE_MAP: Record<string, string> = {
  Q: '上班簽到',
  S: '外出簽退',
  T: '進入簽到',
  V: '下班簽退',
};

const PunchLogTab: React.FC<PunchLogTabProps> = React.memo(({
  punchLogs,
  punchLogTotal,
  loading,
  onLoad,
}) => {
  const [empCode, setEmpCode] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const doSearch = useCallback(() => {
    const params: {
      employee_code?: string;
      date_from?: string;
      date_to?: string;
      page?: number;
      size?: number;
    } = { page, size: pageSize };
    if (empCode.trim()) params.employee_code = empCode.trim();
    if (dateRange[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]) params.date_to = dateRange[1].format('YYYY-MM-DD');
    onLoad(params);
  }, [empCode, dateRange, page, onLoad]);

  useEffect(() => {
    doSearch();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      title: '員工代號', dataIndex: 'f911_01', key: 'f911_01', width: 100,
    },
    {
      title: '日期', dataIndex: 'f911_02', key: 'f911_02', width: 120,
      render: (v: string | null) => v ? v.substring(0, 10) : '',
    },
    {
      title: '時間', dataIndex: 'f911_03', key: 'f911_03', width: 100,
    },
    {
      title: '類型', dataIndex: 'f911_11', key: 'f911_11', width: 100,
      render: (v: string) => LOG_CODE_MAP[v] || v,
    },
    {
      title: '班別', dataIndex: 'f911_12', key: 'f911_12', width: 80,
    },
    {
      title: '卡號', dataIndex: 'f911_13', key: 'f911_13', width: 80,
    },
  ];

  return (
    <div className="punch-log-tab">
      <div className="search-bar">
        <Space wrap>
          <Input
            placeholder="員工代號"
            value={empCode}
            onChange={(e) => setEmpCode(e.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => { setPage(1); doSearch(); }}
          >
            查詢
          </Button>
        </Space>
      </div>
      <Table
        dataSource={punchLogs}
        columns={columns}
        rowKey={(r) => `${r.f911_01}-${r.f911_02}-${r.f911_03}`}
        loading={loading}
        size="small"
        scroll={{ x: 600 }}
        pagination={{
          current: page,
          pageSize,
          total: punchLogTotal,
          onChange: (p) => setPage(p),
          showTotal: (total) => `共 ${total} 筆`,
          showSizeChanger: false,
        }}
      />
    </div>
  );
});

PunchLogTab.displayName = 'PunchLogTab';

export default PunchLogTab;
